import React, {useEffect, useState} from 'react';
import {StyleSheet, TouchableOpacity, View, ViewStyle} from 'react-native';
import {faMinus} from '@fortawesome/free-solid-svg-icons/faMinus';
import {faPlus} from '@fortawesome/free-solid-svg-icons/faPlus';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {observer} from 'mobx-react-lite';

import {AUD} from '../../constants/GlobalConstants';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventSourceType} from '../../utils/analytics/events';
import Text from '../ui-controls/Text';

type CartButtonProps = {
  item: OrderLine;
  size?: number;
  innerStyle?: ViewStyle;
  onAddItem?: () => void;
  disabled?: boolean;
};

const CartButton = observer(
  ({
    size = 24,
    innerStyle,
    item,
    onAddItem,
    disabled = false,
  }: CartButtonProps) => {
    const {shopSelectionStore, cartStore} = useStores();

    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);

    // Update the count value when it has been changed on
    // another screen.
    useEffect(() => {
      const qty = cartStore.getItemQty(item.product_id);
      if (qty >= 1) {
        setCount(qty);
        setOpen(true);
      } else {
        setCount(qty);
        setOpen(false);
      }
    }, [cartStore, item.product_id, cartStore.currentOrder]);

    const addItem = () => {
      if (cartStore.updatingOrder) {
        return null;
      }

      if (onAddItem) {
        onAddItem();
      } else {
        const newCount = count + 1;

        cartStore.setItemQty(item.product_id, newCount);
        setCount(newCount);
        setOpen(true);

        Analytics.logEvent({
          eventName: EventName.AddToCart,
          parameters: {
            source_type: EventSourceType.Button,
            value: item.sell_price_cents / 100,
            currency: AUD,
            items: [
              {
                item_id: item.product_id,
                item_name: item.name,
                item_brand: item.brand_name,
                price: item.sell_price_cents / 100,
                quantity: newCount,
                discount:
                  (item.normal_price_cents - item.sell_price_cents) / 100,
              },
            ],
          },
        });
      }
    };

    const removeItem = () => {
      if (cartStore.updatingOrder) {
        return null;
      }
      const countNew = count - 1;
      if (countNew <= 0) {
        setCount(0);
        setOpen(false);
      } else {
        setCount(countNew);
      }
      cartStore.setItemQty(item.product_id, countNew);
    };

    if (shopSelectionStore.ecommerceDisabled) {
      return (
        <TouchableOpacity onPress={addItem} disabled>
          <View style={styles.cartButtonDisabled}>
            <Text style={styles.shopInstore}>SHOP</Text>
            <Text style={styles.shopInstore}>INSTORE</Text>
          </View>
        </TouchableOpacity>
      );
    }

    if (open) {
      return (
        <View style={styles.containerStyle}>
          <View style={styles.buttonHolder}>
            <TouchableOpacity
              onPress={removeItem}
              style={[styles.button, innerStyle]}>
              <FontAwesomeIcon icon={faMinus} size={size} style={styles.icon} />
            </TouchableOpacity>
            <View style={styles.countHolder}>
              <Text style={styles.count}>{count}</Text>
            </View>
            <TouchableOpacity
              onPress={addItem}
              style={[styles.button, innerStyle]}>
              <FontAwesomeIcon icon={faPlus} size={size} style={styles.icon} />
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    return (
      // <View style={styles.buttonHolder}>
      <TouchableOpacity
        disabled={disabled}
        onPress={addItem}
        testID="addToCartButton">
        <View style={[styles.cartButton, innerStyle]}>
          <Text style={styles.addText}>ADD TO CART</Text>
        </View>
      </TouchableOpacity>
      // </View>
    );
  },
);

export default CartButton;

const styles = StyleSheet.create({
  icon: {
    color: 'white',
  },
  buttonHolder: {
    flexDirection: 'row',
    alignItems: 'center',
    // justifyContent: 'flex-end',
    width: 120,
  },
  countHolder: {
    flex: 2,
    borderWidth: 0.5,
    borderColor: 'lightgrey',
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
  },
  count: {
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: 16,
  },
  button: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 4,
    backgroundColor: theme.colour.background.button.primary,
  },
  containerStyle: {
    alignItems: 'center',
  },
  cartButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 30,
    backgroundColor: theme.colour.background.button.primary,
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  cartButtonDisabled: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 4,
    borderColor: theme.colour.border.general.primary,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  addText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'white',
  },
  shopInstore: {
    fontSize: 12,
    fontWeight: 'bold',
    color: theme.colour.text.heading.primary,
  },
});
